/**
 * CalendarControl — the Calendar Control prototype running in the rail
 * (2026-09-14, KP; rail port stages 1–2).
 *
 * Data: bound (getMyCalendar + the OPS allow-list below). Events-only My Day
 * (events.myday, 2026-10-02) narrows both; see EVENT_OPS / onlyEvents.
 *
 * One surface, four routes. The screen renders the prototype ITSELF, a patched
 * copy at app/public/calendar-control/control.dc.html, chromeless — layout,
 * brand and behaviour are the prototype's own, byte-faithful and deliberately
 * NOT the app design system. Same sanctioned exception, and the same embed
 * pipeline, as FloorCalendar's stage A (docs/design-baseline.md).
 *
 * Three seams only, all in the copy:
 *   ?chromeless=1  drops the board's brand bar; the app shell owns chrome.
 *   ?mode=         the entry mode, one per route.
 *   postMessage    the board's tab strip reports mode changes so the rail
 *                  selection and the URL follow; the host echoes the mode back
 *                  when the rail moves.
 *
 * Writes (2026-09-15, §3 step 2): every action on the board runs a real operation
 * through this host over a fifth seam — `calendar-control-op` in,
 * `calendar-control-op-result` back. The board owns no client and no token; it
 * name an operation from the allow-list in OPS below and gets the server's
 * answer or the server's error. The allow-list is the sixteen the outbox named,
 * plus startServingAdminBooking and listQueues (CD's corrections of 2026-09-15),
 * plus listEventGuests, bulkCheckInGuests and updateGuest (the dockets of * 2026-09-16 — the event roster is a READ and it was missing; the staff check-in
 * is bulkCheckInGuests, and its undo is updateGuest), plus the appointment EDIT
 * and the own cancel (kit 735b29f0, 2026-09-17: updateOwnAppointment /
 * updateAdminAppointment / cancelOwnAppointment / cancelAdminAppointment), plus
 * the queue console's own set and the two create-sheet reads (round 4,
 * 2026-09-17: callNextQueueEntry, recallQueueEntry, serveQueueEntry,
 * recordQueueEntryOutcome, leaveAdminQueueEntry, setStaffQueueAcceptance,
 * listEventTickets), plus getEventTickets — the event's registration tiers for the
 * + Event form, which listEventTickets (sponsor allocations) never was.
 * `documentOwnAppointment` and `documentAdminBooking` are DEPRECATED on the
 * platform and are no longer bound here at all — notes and outcome are fields of
 * the edit. An unknown callId is refused here, not guessed at.
 *
 * Three of them are barrel exports and are called by name. The rest are live
 * routes with no exported wrapper in this build (the wiring kit resolves them to
 * functions the package does not re-export, or to none at all), so they go through
 * the client's own `api()` helper with the path from the wiring kit and the callId
 * as the operation label — the same request the missing wrapper would make, with
 * the same auth, tenant routing and telemetry.
 *
 * Data (2026-09-15, kit 135d05dd): My Day and My Calendar are BOUND — this host
 * calls `getMyCalendar` (GET /v1/me/calendar) with the typed client and posts the
 * payload into the board, which renders both self modes from it. A fourth seam:
 *   calendar-control-range → the board asks for the window it is showing
 *   calendar-control-data / -data-error → the answer, or an honest failure
 * The read is session-scoped (`staffId` is REFUSED, not ignored), so the app
 * shell is the only caller that can make it: the board is a document in an
 * iframe with no access to the typed client. Floor and Schedule still render the
 * prototype's own data — this read does not cover another member's day by design,
 * and the rostered queue-duty block has no server object at all (CC → CD
 * 2026-09-15 §4 R1/R2).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  isApiError,
  api, apiRaw, getMyCalendar, checkInAdminAppointment, createAdminAppointment, listBookingCustomers,
  getCustomer, getCustomerServiceRecord,
  startServingAdminBooking, completeAdminBooking, markAdminBookingNoShow,
  checkInOwnAppointment, startServingOwnAppointment, completeOwnAppointment,
  markOwnAppointmentNoShow, updateOwnAppointment, cancelOwnAppointment,
  updateAdminAppointment, cancelAdminAppointment, listAdminApptSlots,
  admin, getGeneralSettings,
} from "@v0y4ge/api-client";
import { getDateTimeConfig, getWorkspaceDatePattern, useDateTimeVersion } from "../../lib/datetime";
import { readStudioWorkspaceWeekStart } from "../../lib/workspace-datetime";
import { Screen } from "../../shell/chrome";
import { Banner, Btn, Skeleton } from "@v0y4ge/jrni-ui";
import { t } from "../../lib/i18n";
import { useNav } from "../../shell/router";
import { moduleKnownAbsent, useTenantModules } from "../../lib/tenant-modules";
import { useRailCatalog } from "../../shell/chrome-rail";
import { useCustomerNoun } from "../customers/customers-shared";
import { can, getConfirmedTenantSlug, getTenantGeneration, isSignedIn, onTenantChange, onTenantIdentityResolved, onTenantSwitching, subscribeAccess, useUser } from "../../shell/access";

export type CalendarMode = "myday" | "floor" | "schedule" | "mine";
const CALENDAR_BOOT_TIMEOUT_MS = 20_000;

/** The getMyCalendar payload narrowed to Studio events (events-only My Day). positions
 *  is parallel to events, so it is filtered by the same index. */
function onlyEvents(payload: unknown): unknown {
  const p = payload as { events?: Array<{ entryType?: string }>; positions?: unknown[]; blocks?: unknown[]; total?: number } | null;
  if (!p || !Array.isArray(p.events)) return payload;
  const keep = p.events.map((e) => e?.entryType === "event");
  const events = p.events.filter((_, i) => keep[i]).map((e) => {
    const n = Number((e as { pendingApprovalCount?: number }).pendingApprovalCount) || 0;
    const ev = e as { subtitle?: string; title?: string };
    /* Reservation events show what is waiting on the agenda (2026-10-02) — the board renders title/subtitle. */
    return n > 0 ? { ...e, subtitle: [ev.subtitle, n === 1 ? "1 request awaiting approval" : n + " requests awaiting approval"].filter(Boolean).join(" · ") } : e;
  });
  return { ...p, events, blocks: [], total: events.length,
    ...(Array.isArray(p.positions) ? { positions: p.positions.filter((_, i) => keep[i]) } : {}) };
}

/* The board's view and anchor date in this route's query (wave 2a, #7 / #8):
 * `?view=day|week|month` (My Calendar) and `?date=YYYY-MM-DD` (My Calendar and
 * My Day). Strictly shaped here; the board re-validates the date. */
const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const VIEWS = new Set(["day", "week", "month"]);
const paramOf = (params: Record<string, unknown>, key: string): string => (typeof params[key] === "string" ? (params[key] as string) : "");

function CalendarControlLoading({ mode, view, width }: { mode: CalendarMode; view: "day" | "week" | "month"; width: number }) {
  const month = mode === "mine" && view === "month";
  const week = mode === "mine" && view === "week";
  const myDay = mode === "myday";
  const phone = width < 760;
  const responsiveClass = phone ? "phone" : width < 1200 ? "narrow" : "wide";
  const gutter = phone ? 56 : 74;
  const availableWeekWidth = width - gutter - (phone ? 32 : 46);
  const weekWindowColumns = availableWeekWidth / 7 >= (phone ? 112 : 128) ? 7 : Math.max(3, Math.min(6, Math.floor(availableWeekWidth / (phone ? 112 : 180))));
  const columns = month ? 7 : week ? weekWindowColumns : 1;
  const timeGrid = mode === "mine" && view !== "month";
  const gridColumns = month ? `repeat(7,minmax(0,1fr))` : timeGrid ? `${gutter}px repeat(${columns},minmax(0,1fr))` : `repeat(${columns},minmax(0,1fr))`;
  const rows = month ? 5 : 6;
  return <div className={`my-calendar-loading my-calendar-loading--${responsiveClass}`} data-responsive-layout={responsiveClass} role="status" aria-live="polite" aria-busy="true" aria-label={t("common.states.loading", "Loading calendar")} style={{ background: "var(--jrni-color-surface-canvas)" }}>
    <style>{`.my-calendar-loading{box-sizing:border-box;position:absolute;inset:0;z-index:2;overflow:hidden;padding:16px;display:flex;flex-direction:column;gap:12px;color:var(--jrni-color-text-primary,inherit)}.my-calendar-loading__toolbar{flex:none;min-height:44px;display:flex;align-items:center;gap:12px;flex-wrap:wrap;padding-bottom:10px;border-bottom:1px solid var(--jrni-color-surface-border)}.my-calendar-loading__toolbar-main{display:flex;align-items:center;gap:9px;min-height:38px;flex:1 1 100%}.my-calendar-loading__toolbar-actions{display:flex;align-items:center;gap:8px;flex:1 1 100%}.my-calendar-loading__phone-extra{display:none}.my-calendar-loading__summary{flex:none;min-height:118px;height:147px;box-sizing:border-box;border-radius:8px;background:var(--jrni-color-primary-1);color:var(--jrni-color-brand-on-primary);padding:16px;display:grid;grid-template-columns:repeat(4,minmax(0,1fr));box-shadow:var(--jrni-shadow-chrome,0 2px 8px rgba(20,20,40,.12))}.my-calendar-loading__summary-item{min-width:0;padding:0 14px;border-left:1px solid color-mix(in srgb,var(--jrni-color-brand-on-primary) 18%,transparent);display:flex;flex-direction:column;justify-content:space-between}.my-calendar-loading__summary-item:first-child{border-left:0;padding-left:0}.my-calendar-loading__body{flex:1 1 0;min-height:0;overflow:hidden;background:var(--jrni-color-surface-card);border:1px solid var(--jrni-color-surface-border);border-radius:8px}.my-calendar-loading__grid{display:grid;height:100%;min-width:0;grid-template-rows:58px repeat(6,minmax(64px,1fr));overflow:hidden}.my-calendar-loading__head{display:flex;align-items:center;justify-content:center;border-bottom:1px solid var(--jrni-color-surface-border);border-right:1px solid var(--jrni-color-surface-border);min-width:0}.my-calendar-loading__slot{box-sizing:border-box;position:relative;border-bottom:1px dashed var(--jrni-color-surface-border);border-right:1px solid var(--jrni-color-surface-border);padding:8px;min-width:0}.my-calendar-loading__time{border-right:1px solid var(--jrni-color-surface-border);border-bottom:1px solid var(--jrni-color-surface-border);display:flex;align-items:flex-start;justify-content:flex-end;padding:5px 8px;color:var(--jrni-color-text-soft);font-size:12px}.my-calendar-loading__month{display:grid;height:100%;grid-template-columns:repeat(7,minmax(0,1fr));grid-template-rows:34px repeat(5,minmax(64px,1fr));overflow:hidden}.my-calendar-loading__monthday{padding:8px;border-right:1px solid var(--jrni-color-surface-border);border-bottom:1px solid var(--jrni-color-surface-border);min-width:0}.my-calendar-loading__myday{height:100%;overflow:hidden;padding:12px 16px}.my-calendar-loading__myday-head{height:72px;border-bottom:1px solid var(--jrni-color-surface-border);display:flex;align-items:center;gap:12px}.my-calendar-loading__myday-row{min-height:74px;display:flex;align-items:flex-start;gap:12px;padding:12px 4px;border-bottom:1px solid var(--jrni-color-surface-border)}.my-calendar-loading--narrow{padding:16px 18px}.my-calendar-loading--narrow .my-calendar-loading__summary{height:147px}.my-calendar-loading--narrow .my-calendar-loading__summary-item{padding-right:8px;padding-left:8px}.my-calendar-loading--phone{padding:10px;gap:10px}.my-calendar-loading--phone .my-calendar-loading__toolbar{gap:7px;padding-bottom:10px}.my-calendar-loading--phone .my-calendar-loading__toolbar-main{height:34px;min-height:34px;gap:6px}.my-calendar-loading--phone .my-calendar-loading__toolbar-actions{height:36px;min-height:36px;gap:6px}.my-calendar-loading--phone .my-calendar-loading__phone-extra{display:flex;align-items:center;gap:8px;flex:1 1 100%;height:34px;min-height:34px}.my-calendar-loading--phone .my-calendar-loading__summary{height:118px;padding:14px 16px;grid-template-columns:minmax(0,1fr)}.my-calendar-loading--phone .my-calendar-loading__summary-item:not(:first-child){display:none}.my-calendar-loading--phone .my-calendar-loading__summary-item{padding:0;border:0}.my-calendar-loading--phone .my-calendar-loading__grid{grid-template-rows:72px repeat(6,minmax(64px,1fr))}.my-calendar-loading--phone .my-calendar-loading__myday{padding:10px}.my-calendar-loading--phone .my-calendar-loading__myday-head{height:80px}.my-calendar-loading--phone .my-calendar-loading__month{grid-template-rows:32px repeat(5,minmax(54px,1fr))}.my-calendar-loading--phone .my-calendar-loading__monthday{padding:4px}`}</style>
    <div className="my-calendar-loading__toolbar" aria-hidden="true">
      <div className="my-calendar-loading__toolbar-main"><Skeleton variant="rect" width={width < 760 ? 34 : 38} height={34} /><Skeleton variant="rect" width={width < 760 ? 64 : 72} height={34} /><Skeleton variant="rect" width={width < 760 ? 34 : 38} height={34} /><Skeleton variant="text" width={width < 760 ? "42%" : 188} height={16} /></div>
      <div className="my-calendar-loading__toolbar-actions"><Skeleton variant="rect" width={week ? 98 : 82} height={34} /><span style={{ flex: 1 }} /><Skeleton variant="rect" width={width < 760 ? "100%" : 154} height={34} /></div>
      <div className="my-calendar-loading__phone-extra" aria-hidden="true"><Skeleton variant="rect" width="48%" height={32} /><Skeleton variant="rect" width="32%" height={32} /></div>
      <div className="my-calendar-loading__phone-extra" aria-hidden="true"><Skeleton variant="rect" width="45%" height={32} /></div>
    </div>
    {!myDay && <div className="my-calendar-loading__summary" aria-hidden="true">
      <div className="my-calendar-loading__summary-item"><Skeleton variant="text" width={week ? 72 : 88} height={12} /><Skeleton variant="rect" width={56} height={28} /><Skeleton variant="text" width={104} height={11} /></div>
      {[0, 1, 2].map((i) => <div className="my-calendar-loading__summary-item" key={i}><Skeleton variant="text" width={i === 1 ? 88 : 74} height={12} /><Skeleton variant="rect" width={48} height={22} /><div style={{ display: "flex", gap: 4 }}>{Array.from({ length: 5 }, (_, j) => <Skeleton key={j} variant="rect" width={6} height={j % 2 ? 14 : 18} />)}</div></div>)}
    </div>}
    <div className="my-calendar-loading__body" aria-hidden="true">
      {myDay ? <div className="my-calendar-loading__myday"><div className="my-calendar-loading__myday-head"><div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}><Skeleton variant="text" width={80} height={11} /><Skeleton variant="text" width={168} height={18} /></div><Skeleton variant="rect" width={96} height={24} /></div>{Array.from({ length: 5 }, (_, i) => <div className="my-calendar-loading__myday-row" key={i}><div style={{ width: 42, flex: "none", paddingTop: 2 }}><Skeleton variant="text" width={34} height={12} /></div><Skeleton variant="circle" width={10} height={10} /><div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}><Skeleton variant="text" width={i % 2 ? "58%" : "72%"} height={14} /><Skeleton variant="text" width={i % 2 ? "34%" : "46%"} height={11} /></div></div>)}</div>
        : month ? <div className="my-calendar-loading__month" style={{ gridTemplateRows: `34px repeat(${rows},minmax(54px,1fr))` }}>{Array.from({ length: 7 }, (_, i) => <div className="my-calendar-loading__head" key={`h${i}`}><Skeleton variant="text" width={28} height={11} /></div>)}{Array.from({ length: rows * 7 }, (_, i) => <div className="my-calendar-loading__monthday" key={i}><Skeleton variant="text" width={18} height={11} />{i % 3 === 0 && <div style={{ marginTop: 8 }}><Skeleton variant="rect" width="90%" height={16} /></div>}</div>)}</div>
        : <div className="my-calendar-loading__grid" style={{ gridTemplateColumns: gridColumns, gridTemplateRows: `58px repeat(${rows},minmax(64px,1fr))` }}>{timeGrid && <div className="my-calendar-loading__head" style={{ justifyContent: "flex-end", padding: 8 }}><Skeleton variant="text" width={28} height={11} /></div>}{Array.from({ length: columns }, (_, i) => <div className="my-calendar-loading__head" key={`h${i}`}><div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}><Skeleton variant="text" width={42} height={12} /><Skeleton variant="rect" width={56} height={10} /></div></div>)}{Array.from({ length: rows * (columns + (timeGrid ? 1 : 0)) }, (_, i) => { const col = i % (columns + (timeGrid ? 1 : 0)); return timeGrid && col === 0 ? <div className="my-calendar-loading__time" key={`t${i}`}><Skeleton variant="text" width={32} height={11} /></div> : <div className="my-calendar-loading__slot" key={`s${i}`} />; })}</div>}
    </div>
  </div>;
}

/* Mode ↔ route ↔ rail item. The prototype holds the same table; these two must
 * agree, which is why both name the existing ids rather than inventing any. */
const ROUTE_OF: Record<CalendarMode, string> = {
  myday: "workforce.myday",
  floor: "workforce.floor",
  schedule: "workforce.schedule",
  mine: "workforce.myschedule",
};
const RAIL_OF: Record<CalendarMode, string> = {
  myday: "wf-myday",
  floor: "wf-floor",
  schedule: "wf-schedule",
  mine: "wf-mysched",
};

/* EVENTS-ONLY My Day (2026-10-02): Event Manager / Coordinator / Approver get My Day
 * from their own rail item (ev-myday -> events.myday). Same board, same getMyCalendar
 * read (authenticated-session, no capability), narrowed HERE before it reaches the
 * board: only entryType "event" rows go in, closures stay out, appointment create /
 * edit / cancel are reported as not permitted, and the op seam refuses everything
 * that is not an event op. The board's mode tabs cannot carry this persona into the
 * workforce routes. */
const EVENT_OPS = new Set([
  "listEventGuests", "bulkCheckInGuests", "updateGuest", "addWalkupGuest", "checkInRegistrationGuest",
  "getEventTickets", "listEventTickets", "listEvents", "resolveOwnCalendarEntry", "checkInOwnCalendarEntry",
  "getCustomer", "getCustomerServiceRecord",
]);

/* A FOCUS INTENT CROSSES THE REMOUNT (wave 4, JAI-2149). A Week / Day heading opened with Enter or Space
 * asks My Day to put keyboard focus on its heading; the route change remounts this component and its
 * frame, so the intent waits here. The next mount's first render takes it, and that frame's src carries
 * it once (`&focus=myday-heading`). */
let pendingBoardFocus = "";
/* THE WORKSPACE'S WEEK START (wave 4b/4c, JAI-2194 #45). Admins retain the Settings > General read;
 * members use the shared, tenant-fenced public theme read because they do not have settings.read. The first
 * time in a session that a member opens the board, that shared read is made and kept here, so every later mount builds its src with it
 * straight away. Each later mount then asks again IN THE BACKGROUND and, if the workspace has changed it
 * since, moves the open board with calendar-control-locale; it never waits for that read. A refused or
 * failed read is not kept: the known value stays, or the next mount asks again. */
let workspaceWeekStart = "";
let weekStartSettled = false;
let weekStartRead: Promise<string | null> | null = null;
let weekStartCheck: Promise<string | null> | null = null;
/* Set when the session's first wait ran out before the read answered (wave 4c): a read that never settles
 * costs the session one wait, not one per mount. Later mounts open at once and take a late answer as a move. */
let weekStartWaitSpent = false;
/* Every accepted value and every pending read belongs to this cache epoch AND confirmed tenant generation. */
let weekStartSession: object = {};
let weekStartOwner: { slug: string; generation: number } | null = null;
let calendarSessionEpoch = 0;
const WEEK_STARTS = new Set(["monday", "sunday", "saturday"]);
const WEEK_START_WAIT_MS = 400;
function clearWorkspaceWeekStartSession(): void {
  calendarSessionEpoch++;
  pendingBoardFocus = "";
  workspaceWeekStart = "";
  weekStartSettled = false;
  weekStartRead = null;
  weekStartCheck = null;
  weekStartWaitSpent = false;
  weekStartOwner = null;
  weekStartSession = {};
}
/* access.ts fires these synchronously. Purge first; the mounted host resumes only on a confirmed signal. */
onTenantSwitching(clearWorkspaceWeekStartSession);
onTenantChange(clearWorkspaceWeekStartSession);
onTenantIdentityResolved(clearWorkspaceWeekStartSession);
let previousSignedIn = isSignedIn();
subscribeAccess(() => {
  const signedIn = isSignedIn();
  if (previousSignedIn && !signedIn) clearWorkspaceWeekStartSession();
  previousSignedIn = signedIn;
});
/** TEST SEAM ONLY: forget all tenant-scoped session state. */
export function resetCalendarControlSessionForTests(): void {
  clearWorkspaceWeekStartSession();
  previousSignedIn = isSignedIn();
}
function isWeekStartReadCurrent(session: object, slug: string, generation: number): boolean {
  return session === weekStartSession && isSignedIn()
    && getConfirmedTenantSlug() === slug && getTenantGeneration() === generation;
}
/** Do not even invoke Settings until the request's tenant/generation is still confirmed. */
function fetchWorkspaceWeekStart(session: object, slug: string, generation: number, refresh = false): Promise<string | null> {
  if (!slug || !isWeekStartReadCurrent(session, slug, generation)) return Promise.resolve(null);
  if (!can("settings.read")) return readStudioWorkspaceWeekStart(refresh).then((r) => r.ok ? r.weekStartsOn : null);
  return Promise.resolve()
    .then(() => {
      if (!isWeekStartReadCurrent(session, slug, generation) || !can("settings.read")) return null;
      const baseUrl = (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "";
      return getGeneralSettings({ baseUrl } as never) as Promise<unknown>;
    })
    .then((g) => {
      if (g === null || !isWeekStartReadCurrent(session, slug, generation)) return null;
      const v = (g as { general?: { weekStartsOn?: unknown } } | null)?.general?.weekStartsOn;
      return typeof v === "string" && WEEK_STARTS.has(v) ? v : "";
    });
}
function readWorkspaceWeekStart(): Promise<string | null> {
  const slug = getConfirmedTenantSlug();
  const generation = getTenantGeneration();
  if (!slug || !isSignedIn()) return Promise.resolve(null);
  if (weekStartSettled && weekStartOwner?.slug === slug && weekStartOwner.generation === generation) {
    return Promise.resolve(workspaceWeekStart);
  }
  if (weekStartRead) return weekStartRead;
  const session = weekStartSession;
  const read: Promise<string | null> = fetchWorkspaceWeekStart(session, slug, generation)
    .then((v) => {
      if (v === null || !isWeekStartReadCurrent(session, slug, generation)) {
        if (weekStartRead === read) weekStartRead = null;
        return null;
      }
      workspaceWeekStart = v;
      weekStartSettled = true;
      weekStartOwner = { slug, generation };
      return v;
    })
    .catch(() => {
      if (weekStartRead === read) weekStartRead = null;
      return isWeekStartReadCurrent(session, slug, generation) ? "" : null;
    });
  weekStartRead = read;
  return read;
}
function recheckWorkspaceWeekStart(): Promise<string | null> {
  const slug = getConfirmedTenantSlug();
  const generation = getTenantGeneration();
  if (!slug || !isSignedIn()) return Promise.resolve(null);
  if (weekStartCheck) return weekStartCheck;
  const session = weekStartSession;
  const check: Promise<string | null> = fetchWorkspaceWeekStart(session, slug, generation, true)
    .then((v) => {
      if (v === null || !isWeekStartReadCurrent(session, slug, generation)) return null;
      workspaceWeekStart = v;
      weekStartSettled = true;
      weekStartOwner = { slug, generation };
      return v;
    }, () => null)
    .then((v) => { if (weekStartCheck === check) weekStartCheck = null; return v; });
  weekStartCheck = check;
  return check;
}
/** The board's locale: the workspace date pattern and clock (lib/datetime, set from that one sign-in read
 *  and printed by every Studio screen), plus the week start when this session could read it. */
function boardLocale(): { dateFormat?: string; timeFormat?: "12h" | "24h"; weekStartsOn?: string } {
  const out: { dateFormat?: string; timeFormat?: "12h" | "24h"; weekStartsOn?: string } = {};
  const pattern = getWorkspaceDatePattern();
  if (pattern) out.dateFormat = pattern;
  const cfg = getDateTimeConfig();
  let h12 = cfg.hour12;
  if (h12 === undefined) { try { h12 = new Intl.DateTimeFormat(cfg.locale, { hour: "numeric" }).resolvedOptions().hour12; } catch { h12 = undefined; } }
  if (h12 !== undefined) out.timeFormat = h12 ? "12h" : "24h";
  if (weekStartSettled) out.weekStartsOn = workspaceWeekStart || "monday";
  return out;
}

export default function CalendarControl({ mode, scope }: { mode: CalendarMode; scope?: "events" }) {
  const eventsOnly = scope === "events";
  /* The board offers booking / queue only when the tenant composes them, and names the
     tenant's customers in its own words (CC, 2026-10-03). Unknown modules = both offered. */
  const tenantModules = useTenantModules();
  const bookingComposed = !moduleKnownAbsent("booking", tenantModules);
  const queueComposed = !moduleKnownAbsent("queue", tenantModules);
  /* The noun the shell already names this tenant's customers with: its served rail item. A role without the
     Customers item (no customers.read) gets the shared customer noun (CC, 2026-10-04): an empty label made the
     board fall back to its own "Students", so an events-only coordinator's search read "Student name". */
  const { nounPlural: customerNoun } = useCustomerNoun();
  const customersLabel = useRailCatalog().find((i) => i.route === "customers")?.label || customerNoun;
  /* Events scope has two routes of its own (2026-10-05): My Calendar → events.mycalendar, everything else → events.myday. */
  const routeOf = (m: CalendarMode) => (eventsOnly ? (m === "mine" ? "events.mycalendar" : "events.myday") : ROUTE_OF[m]);
  const nav = useNav();
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  /* The view|date values the board asked the URL to carry, oldest first, until
   * each has come back through the route (wave 2a, #8). */
  const boardRouteRef = useRef<string[]>([]);
  const [ready, setReady] = useState(false);
  const readyRef = useRef(ready);
  readyRef.current = ready;
  const [bootFailure, setBootFailure] = useState<"timeout" | "load-error" | null>(null);
  const [bootAttempt, setBootAttempt] = useState(0);
  const bootAttemptRef = useRef(bootAttempt);
  bootAttemptRef.current = bootAttempt;
  const user = useUser();
  const canCreateAppointment = !eventsOnly && can("services.read") && can("customers.read") && can("appointments.create");
  /* My Calendar's appointment sheet offers Reschedule / Cancel appointment only on
   * these grants (wave 2a, #16) — the capabilities of updateOwnAppointment
   * (appointments.edit_own) and cancelOwnAppointment (appointments.cancel_own).
   * The board uses canEditOwnAppointment only to offer Reschedule, and saving a
   * reschedule changes the appointment window, which the booking route refuses
   * (403 APPOINTMENT_RESCHEDULE_FORBIDDEN) unless the member also holds
   * appointments.reschedule_own — so Reschedule needs both grants. */
  const canEditOwnAppointment = !eventsOnly && can("appointments.edit_own") && can("appointments.reschedule_own");
  const canCancelOwnAppointment = !eventsOnly && can("appointments.cancel_own");

  /* WHAT THE FIRST SRC CARRIES is fixed at the first render (wave 4b): the view and date the URL names
   * (wave 2a, #8) and a focus intent left by the mount before (wave 4, JAI-2149). The src itself may be
   * built a moment later (below), after the effect that spends the intent has run. */
  const [initialSrc] = useState(() => ({
    view: paramOf(nav.params, "view"), date: paramOf(nav.params, "date"),
    focus: mode === "myday" && pendingBoardFocus === "myday-heading", epoch: calendarSessionEpoch,
  }));
  /* THE FIRST READ ASKS FOR THE RIGHT WEEK (wave 4b, JAI-2194 #45). On the session's first visit by a member
   * who may read Settings > General, the src waits for the week start (at most WEEK_START_WAIT_MS), so the
   * board's first range read already starts on the workspace's day instead of opening on Monday and reading
   * again when the answer arrives. Anyone else, every later mount, and every mount after a wait that ran
   * out (wave 4c) open at once. While the src waits, the host shows the same agenda/day/week/month shape
   * the board will open with, using the actual JRNI Skeleton primitive. */
  const [tenantSession, setTenantSession] = useState(() => ({
    slug: getConfirmedTenantSlug(), generation: getTenantGeneration(), signedIn: isSignedIn(), epoch: calendarSessionEpoch,
  }));
  const [srcOpen, setSrcOpen] = useState(() => !!getConfirmedTenantSlug() && isSignedIn()
    && (weekStartSettled || weekStartWaitSpent));
  const [weekStartsOn, setWeekStartsOn] = useState(workspaceWeekStart);
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const update = () => setContainerWidth(node.clientWidth);
    update();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(node);
    window.addEventListener("resize", update);
    return () => { observer?.disconnect(); window.removeEventListener("resize", update); };
  }, []);
  const tenantSessionCurrent = () => tenantSession.signedIn && !!tenantSession.slug
    && isSignedIn() && getConfirmedTenantSlug() === tenantSession.slug
    && getTenantGeneration() === tenantSession.generation && calendarSessionEpoch === tenantSession.epoch;

  useEffect(() => {
    let previousAuth = isSignedIn();
    const suspend = () => {
      liveSeq.current++;
      boardRouteRef.current = [];
      setTenantSession({ slug: null, generation: getTenantGeneration(), signedIn: isSignedIn(), epoch: calendarSessionEpoch });
      readyRef.current = false;
      setSrcOpen(false); setReady(false); setWeekStartsOn(""); setBootFailure(null); setBootAttempt(0);
      frameRef.current?.removeAttribute("src");
    };
    const resumeConfirmed = () => {
      liveSeq.current++;
      boardRouteRef.current = [];
      setTenantSession({ slug: getConfirmedTenantSlug(), generation: getTenantGeneration(), signedIn: isSignedIn(), epoch: calendarSessionEpoch });
      readyRef.current = false;
      setSrcOpen(false); setReady(false); setWeekStartsOn(""); setBootFailure(null); setBootAttempt(0);
    };
    const onAuth = () => {
      const signedIn = isSignedIn();
      if (previousAuth && !signedIn) suspend();
      else if (!previousAuth && signedIn && getConfirmedTenantSlug()) resumeConfirmed();
      previousAuth = signedIn;
    };
    const offSwitching = onTenantSwitching(suspend);
    const offChange = onTenantChange(resumeConfirmed);
    const offIdentity = onTenantIdentityResolved(resumeConfirmed);
    const offAccess = subscribeAccess(onAuth);
    return () => { offSwitching(); offChange(); offIdentity(); offAccess(); };
  }, []);

  useEffect(() => {
    pendingBoardFocus = "";
    const slug = tenantSession.slug;
    const generation = tenantSession.generation;
    const session = weekStartSession;
    const current = () => session === weekStartSession && tenantSessionCurrent()
      && slug === getConfirmedTenantSlug() && generation === getTenantGeneration();
    if (!tenantSession.signedIn || !slug || !current()) {
      readyRef.current = false;
      setReady(false); setSrcOpen(false);
      return;
    }
    let live = true;
    const effectCurrent = () => live && current();
    if (weekStartSettled && weekStartOwner?.slug === slug && weekStartOwner.generation === generation) {
      void recheckWorkspaceWeekStart().then((v) => {
        if (effectCurrent() && v !== null) setWeekStartsOn(v);
      });
      return () => { live = false; };
    }
    const timer = weekStartWaitSpent ? undefined : window.setTimeout(() => {
      if (!effectCurrent()) return;
      weekStartWaitSpent = true;
      setSrcOpen(true);
    }, WEEK_START_WAIT_MS);
    void readWorkspaceWeekStart().then((v) => {
      if (!effectCurrent() || v === null) return;
      window.clearTimeout(timer);
      setWeekStartsOn(v);
      setSrcOpen(true);
    });
    return () => { live = false; window.clearTimeout(timer); };
  }, [tenantSession.slug, tenantSession.generation, tenantSession.signedIn, tenantSession.epoch]);

  /* Public assets resolve through __STUDIO_ASSET_BASE__ (app/public), same
   * mechanism as FloorCalendar — a route-relative src would resolve against
   * /workforce/my-day and 404. Falls back to the Vite site-root convention.
   * Deliberately NOT keyed on `mode`: remounting the iframe on every mode
   * change would throw away the board's state and re-run its boot. The mode is
   * pushed in over postMessage instead. */
  const boardSrc = useMemo(() => {
    if (!srcOpen || !tenantSessionCurrent()) return undefined;
    const base = (window as unknown as { __STUDIO_ASSET_BASE__?: string }).__STUDIO_ASSET_BASE__ ?? "";
    /* The board opens on the view and date the URL names (wave 2a, #8): a
     * refresh, a pasted link or Back onto this route restores them. */
    const sameHostEpoch = initialSrc.epoch === tenantSession.epoch;
    const v0 = sameHostEpoch ? initialSrc.view : paramOf(nav.params, "view");
    const d0 = sameHostEpoch ? initialSrc.date : paramOf(nav.params, "date");
    /* ...and in the workspace's formats and week from its first read (wave 4, JAI-2194 #45). */
    const loc = boardLocale();
    return (base ? base.replace(/\/$/, "") + "/" : "/") + "calendar-control/control.dc.html?chromeless=1&mode=" + mode
      + (mode === "mine" && VIEWS.has(v0) ? "&view=" + v0 : "")
      + ((mode === "mine" || mode === "myday") && DATE_RE.test(d0) ? "&date=" + d0 : "")
      + (sameHostEpoch && initialSrc.focus ? "&focus=myday-heading" : "")
      + (loc.weekStartsOn ? "&weekStart=" + loc.weekStartsOn : "")
      + (loc.timeFormat ? "&timeFormat=" + loc.timeFormat : "")
      + (loc.dateFormat ? "&dateFormat=" + encodeURIComponent(loc.dateFormat) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcOpen, tenantSession.slug, tenantSession.generation, tenantSession.signedIn, tenantSession.epoch]);

  /* The board's calendar read. One request per visible range, answered into the
   * frame; a stale answer is dropped on the board's side by requestId. `view`,
   * `date`, `from` and `to` are the board's — the query carries nothing that
   * selects WHOSE calendar this is, because the schema refuses it (422) and the
   * session is the only source of that. */
  const liveSeq = useRef(0);
  useEffect(() => {
    const post = (msg: Record<string, unknown>) => {
      if (!tenantSessionCurrent()) return;
      const w = frameRef.current?.contentWindow;
      if (!w) return;
      try { w.postMessage(msg, window.location.origin); } catch { /* noop */ }
    };
    const onMsg = (ev: MessageEvent) => {
      if (!tenantSessionCurrent()) return;
      if (ev.origin !== window.location.origin) return;
      if (ev.source !== frameRef.current?.contentWindow) return;
      const m = ev.data as { type?: string; requestId?: number; view?: string; date?: string; from?: string; to?: string } | null;
      if (!m || m.type !== "calendar-control-range") return;
      const token = ++liveSeq.current;
      const q: { view?: "day" | "week" | "month" | "list"; date?: string; from?: string; to?: string } = {};
      if (m.view === "day" || m.view === "week" || m.view === "month" || m.view === "list") q.view = m.view;
      if (m.date) q.date = m.date;
      if (m.from) q.from = m.from;
      if (m.to) q.to = m.to;
      void getMyCalendar(q)
        .then((payload) => { if (token === liveSeq.current) post({ type: "calendar-control-data", requestId: m.requestId, payload: eventsOnly ? onlyEvents(payload) : payload }); })
        .catch((e: unknown) => {
          if (token !== liveSeq.current) return;
          /* The code and status ride along, as on the op seam below, so the board
           * can tell an expired session (401) or a denial (403) from an outage.
           * `message` stays the client's HONEST one and may be '' — the board's cue
           * to show its own copy. `fallbackLabel` is deliberately NOT substituted
           * here: it carries `HTTP 502` or a developer diagnostic (the HTML
           * guard's API base URL), never words for staff. */
          if (isApiError(e)) {
            post({ type: "calendar-control-data-error", requestId: m.requestId,
              message: e.message, code: e.code, status: e.status });
            return;
          }
          post({ type: "calendar-control-data-error", requestId: m.requestId,
            message: e instanceof Error ? e.message : String(e) });
        });
    };
    window.addEventListener("message", onMsg);
    return () => { liveSeq.current++; window.removeEventListener("message", onMsg); };
  }, [tenantSession.slug, tenantSession.generation, tenantSession.signedIn, tenantSession.epoch]);

  /* The board's write seam. Each entry takes the argument bag the board sends and
   * returns the operation's promise; the board gets {ok, data} or {ok:false,
   * error}. Paths are the wiring kit's, verified live in backed-ops.json. */
  useEffect(() => {
    const enc = encodeURIComponent;
    const post = (path: string, body?: unknown, callId?: string) =>
      api(path, { method: "POST", body: JSON.stringify(body ?? {}) }, callId);
    type Args = Record<string, unknown>;
    const s = (a: Args, k: string) => String(a[k] ?? "");
    const PAGE_CEILING = 50;
    const allPages = async (path: string, callId: string, keys: string[]): Promise<unknown> => {
      const rows: unknown[] = [];
      for (let page = 1; page <= PAGE_CEILING; page += 1) {
        if (!tenantSessionCurrent()) throw new Error("tenant session changed");
        const res = (await apiRaw(`${path}&page=${page}`, { method: "GET" }, callId)) as Record<string, unknown> | null;
        if (!tenantSessionCurrent()) throw new Error("tenant session changed");
        const list = keys.map((k) => res?.[k]).find(Array.isArray) as unknown[] | undefined;
        if (!list) throw new Error(`the ${callId} read did not return a list`);
        rows.push(...list);
        /* More pages are flagged at the top level (the staff and events reads) or
         * inside `pagination` (the shared `{ data, pagination }` envelope, e.g. the
         * queue read). Checking only the first would stop silently after page 1. */
        const pagination = res?.pagination as { hasMore?: unknown } | undefined;
        if (res?.hasMore === true || pagination?.hasMore === true) continue;
        const done = pagination && typeof pagination === "object" ? { pagination: { ...pagination, hasMore: false } } : {};
        return { ...res, ...Object.fromEntries(keys.map((k) => [k, rows])), hasMore: false, ...done };
      }
      throw new Error(`the ${callId} read still had more rows after ${PAGE_CEILING} pages`);
    };

    /* ── A LIFECYCLE WRITE MUST COME BACK AS A BOOKING ────────────────────────
     * `booking.ts` → `apiRaw` → `mapMutationResponse` neither throws nor reports
     * on a non-ok, non-JSON response: it RESOLVES `{}`. So `/complete`,
     * `/start-serving` and `/no-show` answered 501/404 and still fulfilled with an
     * empty object — no `error` for an absence-of-error check to catch, which is
     * why the first fix missed exactly these three. `checkInAdminAppointment`
     * (coded envelope) and every other op (they throw) were never affected.
     *
     * The guard is therefore POSITIVE: the server has to hand back the thing it
     * claims to have written — a booking or appointment carrying an `id`. Nothing
     * recognisable means the write is reported as failed, which is the honest
     * answer whether the cause was an unbound route, a 404 or a shape the mapper
     * could not read. */
    const entityOf = (value: unknown): { id?: unknown } | null => {
      if (!value || typeof value !== "object") return null;
      const v = value as Record<string, unknown>;
      for (const key of ["data", "booking", "appointment"]) {
        const nested = v[key];
        if (nested && typeof nested === "object" && (nested as { id?: unknown }).id) return nested as { id?: unknown };
      }
      return v.id ? v : null;
    };
    const mustReturnBooking = async (run: Promise<unknown>): Promise<unknown> => {
      const data = await run;
      if (entityOf(data)) return data;
      const envelope = (data && typeof data === "object" ? data : null) as { error?: { message?: unknown; code?: unknown } } | null;
      const named = envelope?.error;
      throw new Error(
        (named && String(named.message ?? named.code ?? "")) ||
        "the server returned no booking, so nothing was recorded",
      );
    };
    const OPS: Record<string, (a: Args) => Promise<unknown>> = {
      /* ── MY DAY: THE ADVISOR'S OWN APPOINTMENTS ─────────────────────────────
         Kit `kit-delta-2026-09-16-31a59e48` delivers the five own-appointment
         commands, so My Day no longer borrows the admin lane. These run on
         `/v1/me/appointments/{id}/…` and `appointments.edit_own`, verify
         signed-in account → canonical staff → booking assignment, and refuse a
         colleague's appointment. Their result is the canonical AdminAppointment,
         not the admin `{data, reversal}` envelope.

         THERE IS NO FALLBACK TO THE ADMIN CALLS. A permission failure is the
         answer, not a reason to try the privileged route — which is what would
         quietly hand an advisor a workflow the backend refuses them. The board
         picks its lane by mode; the admin ops below stay for the authorized
         privileged flows. The own check-in takes NO `intent` (that property is
         the positional admin contract's), so the board's admin-shaped input is
         deliberately not forwarded here. */
      checkInOwnAppointment: (a) => checkInOwnAppointment(s(a, "bookingId"), { source: "staff", notify: false } as never),
      startServingOwnAppointment: (a) => startServingOwnAppointment(s(a, "bookingId")),
      completeOwnAppointment: (a) => completeOwnAppointment(s(a, "bookingId"), (a.input as never) ?? ({ notify: false } as never)),
      markOwnAppointmentNoShow: (a) => markOwnAppointmentNoShow(s(a, "bookingId"), { notify: false, charge_late_fee: false } as never),

      /* ── THE APPOINTMENT EDIT, AND THE OWN CANCEL (kit 735b29f0) ───────────
         One PATCH per lane carries everything that is a plain FACT about the
         appointment: the venue-local window (reschedule), the note, the
         documented outcome, the three recorded instants and the contact
         channel. `null` on a nullable field clears it; an omitted key leaves it
         alone; unknown keys are refused (strict). The response is the full
         AdminAppointment, so the open sheet adopts what came back rather than
         predicting it — clearing `startedAt` also clears `completedAt` and the
         status is recomputed server-side.

         The documentation-only commands (`documentOwnAppointment` /
         `documentAdminBooking`) are DEPRECATED and deliberately no longer bound
         here: notes and outcome ride this edit. `outcome: 'noshow'` is refused
         by the schema — a no-show stays its own command. */
      updateOwnAppointment: (a) => updateOwnAppointment(s(a, "bookingId"), a.input as never),
      cancelOwnAppointment: (a) => cancelOwnAppointment(s(a, "bookingId"), (a.input as never) ?? ({} as never)),

      /* appointments — the PRIVILEGED lane, for an authorized admin flow only.
         `intent: 'check-in'` stays explicit here: the positional admin check-in
         advances whatever state the booking is in, so a retried call on an
         already-arrived booking can COMPLETE it. */
      checkInAdminAppointment: (a) =>
        checkInAdminAppointment(s(a, "appointmentId"), (a.input as never) ?? ({ intent: "check-in", source: "staff" } as never)),
      startServingAdminBooking: (a) => mustReturnBooking(startServingAdminBooking(s(a, "bookingId"))),
      completeAdminBooking: (a) => mustReturnBooking(completeAdminBooking(s(a, "bookingId"), (a.input as never) ?? ({} as never))),
      markAdminBookingNoShow: (a) => mustReturnBooking(markAdminBookingNoShow(s(a, "bookingId"), (a.input as never) ?? ({} as never))),
      /* The privileged twins of the two above. `cancelAdminAppointment` takes
         the reason positionally, not as a body. */
      updateAdminAppointment: (a) => updateAdminAppointment(s(a, "bookingId"), a.input as never),
      cancelAdminAppointment: (a) => {
        const input = (a.input ?? {}) as { reason?: unknown };
        const reason = input.reason == null ? undefined : String(input.reason);
        return cancelAdminAppointment(s(a, "bookingId"), reason as never);
      },
      createAdminAppointment: (a) => createAdminAppointment(a.input as never),
      listAdminApptSlots: (a) => listAdminApptSlots((a.query ?? {}) as never),
      /* queue */
      callQueueEntry: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/call`, a.input, "callQueueEntry"),
      completeQueueEntry: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/complete`, a.input, "completeQueueEntry"),
      markQueueEntryNoShow: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/no-show`, a.input, "markQueueEntryNoShow"),
      /* THE QUEUE-SCOPED MESSAGE ROUTE. `sendQueueMessage` on `/v1/staff/entries`
         is marked not-for-new-callers; `sendQueueEntryMessage` is the canonical
         one and needs the queue id as well as the entry's. Both stay bound, so an
         older board build does not lose its send. */
      sendQueueEntryMessage: (a) => post(`/v1/admin/queues/${enc(s(a, "queueId"))}/entries/${enc(s(a, "entryId"))}/messages`, a.input, "sendQueueEntryMessage"),
      sendQueueMessage: (a) => post(`/v1/staff/entries/${enc(s(a, "entryId"))}/message`, a.input, "sendQueueMessage"),
      setQueueStatus: (a) => post(`/v1/admin/queues/${enc(s(a, "queueId"))}/status`, a.input, "setQueueStatus"),
      addQueueEntry: (a) => post(`/v1/admin/queues/${enc(s(a, "queueId"))}/entries`, a.input, "addQueueEntry"),
      /* The add-to-queue picker's source: a label is not an id, and there is no
       * entry to read one off before the person is in the line. */
      /* STAFF-SCOPED, NOT THE ADMIN READ (2026-10-03). The admin queue read needs
       * `queues.read`, which frontline My Day roles do not hold. `listStaffPickerQueues`
       * (GET /v1/me/queues) is authorised by `queue.entries.write`, the grant the
       * add itself needs, and returns open queues only: `{ queues: [{ id, name,
       * status, locationId }], total, page, limit, hasMore }`, 200 a page at most.
       * Every page, not the first. */
      listQueues: () => allPages("/v1/me/queues?limit=200", "listStaffPickerQueues", ["queues"]),
      /* The create sheet's directories (2026-09-30, Greg): My Day may book an
       * appointment with ANY staff member and add a student to ANY open queue
       * or live event — not only the ones on the member's own calendar. */
      /* STAFF-SCOPED, NOT THE ADMIN DIRECTORIES (2026-10-03). The admin staff and
       * events reads need `staffing.read` / `events.read`, which frontline My Day
       * roles do not hold. `listStaffPickerColleagues` (GET /v1/me/colleagues, on
       * `appointments.create`) answers `{ staff: [{ id, name }], ... }`;
       * `listStaffPickerEvents` (GET /v1/me/events, on `guests.write`) answers
       * `{ events: [...], ... }` with only scheduled or live events whose
       * registration is open. EVERY PAGE, NOT THE FIRST: both say `hasMore` at the
       * top level while rows remain (200 a page at most), and a directory still not
       * exhausted after the page ceiling is refused rather than shown partial.
       * `apiRaw`, not `api`: `api()` would unwrap an envelope and drop `hasMore`. */
      listStaff: () => allPages("/v1/me/colleagues?limit=200", "listStaffPickerColleagues", ["staff"]),
      listEvents: () => allPages("/v1/me/events?limit=200", "listStaffPickerEvents", ["events"]),
      /* The queue's own public projection — `getPublicQueue`, GET
       * /v1/public/queues/{queueId} (anonymous by contract): name, status and the
       * services its join door accepts. The add form reads a duty's services here
       * because the admin queue reads are not an advisor's. `entryId` is sent only
       * when the caller names one; the form never does. */
      getPublicQueue: (a) => {
        const entryId = s(a, "entryId");
        return api(`/v1/public/queues/${enc(s(a, "queueId"))}${entryId.trim() ? `?entryId=${enc(entryId)}` : ""}`,
          { method: "GET" }, "getPublicQueue");
      },
      /* The Add-appointment service list. The board used to offer the seed's
       * service names per line, which in a self mode is one tenant's
       * catalogue baked into the component — a list that is wrong for every other
       * tenant and carries no service id to book with.
       *
       * THE PATH WAS WRONG (CD round 4, item 4): this called
       * `/v1/admin/services?limit=200`, which is not the contract's route —
       * `listStudioServices` is `GET /v1/admin/catalog/services` (`services.read`).
       * The "403 five times" in CD's trace was this seam asking for a route that
       * is not the operation's; the repeat came from the board re-asking after
       * each failure, which is fixed on its side too. */
      /* EVERY PAGE, NOT THE FIRST. The catalogue read caps a page at 100 whatever
       * `limit` asks for and says `hasMore` while services remain, so a single
       * request dropped every service past the hundredth from the picker with no
       * sign the list was short. Same rule as the staff and event directories. */
      listStudioServices: () => allPages("/v1/admin/catalog/services?limit=100", "listStudioServices", ["services", "items"]),
      listEventTickets: (a) => api(`/v1/admin/events/${enc(s(a, "eventId"))}/ticket-blocks`, { method: "GET" }, "listEventTickets"),
      /* The event's REGISTRATION tiers — `getEventTickets`, GET
         /v1/admin/events/{eventId}/tickets (`guests.read`), answering
         `{ tickets: [{ id, name, desc, price, priceMinor, currency, sold, capacity, kind? }] }`.
         This is what the board's + Event form offers. `listEventTickets` above is
         the sponsor allocation read (`/ticket-blocks`) and is not a tier list. */
      getEventTickets: (a) => api(`/v1/admin/events/${enc(s(a, "eventId"))}/tickets`, { method: "GET" }, "getEventTickets"),
      /* THE QUEUE CONSOLE'S OWN OPERATIONS (CD round 4, item 1). `queue.serve`
         every one; the member persona holds it through `workforce.frontline_member`.
         `serveQueueEntry` starts the visit, `recordQueueEntryOutcome` writes what
         it was AFTER `completeQueueEntry` closes it, `leaveAdminQueueEntry` is
         "they left" as distinct from a no-show, and `setStaffQueueAcceptance` is
         whether the queue may hand you the next person — not a break. */
      callNextQueueEntry: (a) => post(`/v1/admin/queues/${enc(s(a, "queueId"))}/call-next`, a.input, "callNextQueueEntry"),
      recallQueueEntry: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/recall`, a.input, "recallQueueEntry"),
      serveQueueEntry: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/serve`, a.input, "serveQueueEntry"),
      recordQueueEntryOutcome: (a) => post(`/v1/staff/entries/${enc(s(a, "entryId"))}/outcome`, a.input, "recordQueueEntryOutcome"),
      leaveAdminQueueEntry: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/leave`, a.input, "leaveAdminQueueEntry"),
      /* "Return to line" DOES have an operation (CD's round-4 answers): a called
         or being-served entry goes back to waiting. The state machine refuses it
         on anything else, which is the board's guard too. */
      returnQueueEntryToQueue: (a) => post(`/v1/admin/entries/${enc(s(a, "entryId"))}/return-to-queue`, a.input, "returnQueueEntryToQueue"),
      /* THE MEMBER'S OWN OVERRIDE LANE (kit eaf84fb6, `myschedule.write`). The
         admin lane below needs `settings.write`, a tenant right — so Block time on
         My Day / My Calendar uses these four and Floor / Schedule keep the admin
         ops. The server pins the staff from the session; a body naming one is
         refused, so none is sent. The client's export names are `listOwnOverrides`
         / `createOwnOverride` / `updateOwnOverride` / `deleteOwnOverride` — the
         callIds keep the contract's longer names, which is what the board says. */
      /* ── THE SCAN (CD scan docket, 2026-09-17) ──────────────────────────
         Bound on the docket's final routes ahead of the wiring kit, so the board's
         scanner exercises the real seam; until the platform PR is on the tenant
         these answer 404 and the scanner renders that refusal rather than faking a
         resolve. `entryRef` is the id the board reduced a manage URL to.
         `resolveOwnCalendarEntry` carries the effective policy for the entry — the
         board must not read manage settings, which a Team Member cannot. */
      resolveOwnCalendarEntry: (a) => api(`/v1/me/calendar/entries/${enc(s(a, "entryRef"))}`,
        { method: "GET" }, "resolveOwnCalendarEntry"),
      checkInOwnCalendarEntry: (a) => api(`/v1/me/calendar/entries/${enc(s(a, "entryRef"))}/check-in`,
        { method: "POST", body: JSON.stringify(a.input ?? { source: "qr" }) }, "checkInOwnCalendarEntry"),
      listOwnAvailabilityOverrides: (a) => admin.listOwnOverrides((a.query as never) ?? undefined),
      createOwnAvailabilityOverride: (a) => admin.createOwnOverride(a.input as never),
      updateOwnAvailabilityOverride: (a) => admin.updateOwnOverride(s(a, "overrideId"), a.input as never),
      deleteOwnAvailabilityOverride: (a) => admin.deleteOwnOverride(s(a, "overrideId")),
      setStaffQueueAcceptance: (a) => api("/v1/staff/queue-status", { method: "PUT", body: JSON.stringify(a.input ?? {}) }, "setStaffQueueAcceptance"),
      setStaffBreak: (a) => api("/v1/staff/break", { method: "PUT", body: JSON.stringify(a.input ?? {}) }, "setStaffBreak"),
      /* customers — TWO DIFFERENT READS, and the difference is the contract's
         (CD brief item 9). `/v1/customers/search` requires `field=email|phone`
         alongside `q` and cannot search a name; it used to be called with a bare
         name here, which returned nothing while the board reported a match.
         Name/text search is `listBookingCustomers`. A call with no valid field is
         refused here rather than sent as a query the route rejects. */
      listBookingCustomers: (a) => listBookingCustomers({ q: s(a, "q") } as never),
      searchCustomers: (a) => {
        const field = s(a, "field");
        if (field !== "email" && field !== "phone") {
          return Promise.reject(new Error("customers/search needs field=email or field=phone — a name is not searchable there; use listBookingCustomers"));
        }
        return api(`/v1/customers/search?field=${enc(field)}&q=${enc(s(a, "q"))}`, { method: "GET" }, "searchCustomers");
      },
      createCustomer: (a) => post("/v1/customers", a.input, "createCustomer"),
      /* THE CUSTOMER RECORD BEHIND A CALENDAR ENTRY (CC, 2026-09-22).

         Bound because the board could not reach it: this allow-list refuses an
         unknown callId rather than guessing, and it carried only the two
         SEARCHES plus create. So the board had a name, no identifier and no
         read — and generated a profile from a hash of the name, which is how a
         real appointment came to show an invented "Advising hold · pending
         audit". `UnifiedCalendarEvent.customerId` (added this drop) is the
         identifier; these two are the reads it feeds.

         Both are typed exports over `apiRaw`, so they return the response
         SHAPE UNCHANGED — they do NOT unwrap a `data` envelope the way the
         local `api()` helper above does. That difference caused the
         `listEventGuests` "0 of 0" defect in this same file; the board must
         read `payload.data`, not `payload`.

         No stale-answer guard is added here: `runOp` already answers on the
         `calendar-control-op` seam with the caller's `requestId`, which is the
         same discriminator `calendar-control-range` uses via `liveSeq` — a
         late reply is discarded board-side by id rather than raced here. */
      /* THE RECORD A CREATE FORM OPENS ON. Students → a record → Appointment /
         Event / Queue sends the record's canonical id on the create message; the
         form reads it here to show the student already chosen, with the record's
         own name and contact rather than a split display name and a blank email.
         So the answer carries, beside the response unchanged (`data`,
         `redirectTo` — what the panel above reads), the five fields the form
         fills: `id` (`data.customerId`), `firstName` / `lastName`
         (`data.name`), and `email` / `phone` — the `value` of the contact
         point whose `purpose` is "primary", else the first one, else "".
         GET /v1/customers/{customerId} is authorised by `customers.read`, which
         the frontline member bundle holds. A response with no record (an error
         envelope) passes through unchanged, so the seam reports it as a failure;
         a merged record answers with its tombstone's id and `redirectTo`. */
      getCustomer: async (a) => {
        const res = await getCustomer(s(a, "customerId"));
        const rec = (res as { data?: typeof res.data } | null)?.data;
        if (!rec || typeof rec !== "object" || !rec.customerId) return res;
        const pick = (points: Array<{ value: string; purpose: string }> | undefined) =>
          ((Array.isArray(points) ? points.find((p) => p?.purpose === "primary") ?? points[0] : undefined)?.value) ?? "";
        return { ...res, id: rec.customerId, firstName: rec.name?.firstName ?? "", lastName: rec.name?.lastName ?? "",
          email: pick(rec.emails), phone: pick(rec.phones) };
      },
      getCustomerServiceRecord: (a) => getCustomerServiceRecord(s(a, "customerId"), (a.input ?? {}) as never),
      /* own time — create/update/delete all typed now (the kit carries
         `admin.updateOverride` / `admin.deleteOverride`; their call ids are
         `updateAvailabilityOverride` / `deleteAvailabilityOverride`). These are
         ADMIN operations: a denial is surfaced, never worked around. */
      createAvailabilityOverride: (a) => post("/v1/admin/overrides", a.input, "createAvailabilityOverride"),
      updateAvailabilityOverride: (a) => admin.updateOverride(s(a, "overrideId"), a.input as never),
      deleteAvailabilityOverride: (a) => admin.deleteOverride(s(a, "overrideId")),
      /* events — THE ROSTER IS A READ, and it was missing (CD docket 2026-09-16
         item 5). The board generated the attendee list, so "Check in" had no
         registration to address and wrote locally. `eventGuests` is the canonical
         event-scoped guest list, read to the last page, and the board renders
         nothing when the read fails rather than inventing attendees. */
      /* EVERY PAGE, NOT THE FIRST. The guest read caps a page at 100 whatever
         `limit` asks for, and `api()` unwraps the `data` envelope to the bare
         array, which threw away `hasMore` — so an event with more than 100
         registrations showed only the first 100, and the sheet's search, its
         "checked in" count and the door scan's next guest all ran on that
         partial list. `allPages` reads through `apiRaw` until `hasMore` is
         false and returns the body with every row under `guests` and `data`
         (`GuestListResponse.data` is a legacy alias of `guests`), which is
         what the board's `(res.guests || res.data) || []` reads. A roster still
         not exhausted after the page ceiling is refused, not shown partial. */
      listEventGuests: (a) => allPages(`/v1/admin/events/${enc(s(a, "eventId"))}/guests?limit=100`, "eventGuests", ["guests", "data"]),
      /* The REGISTRANT's check-in is the platform's staff check-in
         `bulkCheckInGuests`, one guest id per tap (CD docket 2, 5b): it runs the
         whole flow server-side — status → checked_in, the `registration.checked_in`
         domain event for messaging, the attendance rollup, linked queue entries.
         `checkInRegistrationGuest` below is only the +1-guest variant of a
         registration, not the primary check-in.

         The path is the CONTRACT's, tenant-level with the event id in the body:
         `GUESTS_OPERATIONS.bulkCheckInGuests` is `/v1/admin/guests/bulk/check-in`
         and `eventId` is a field of `GuestBulkSelectionInput`. CD's docket named
         an event-scoped route and then confirmed (2026-09-16) that the contract
         path is canonical and the docket was wrong. */
      bulkCheckInGuests: (a) => post("/v1/admin/guests/bulk/check-in", a.input, "bulkCheckInGuests"),
      /* The registration's own fields, and the UNDO of a check-in: CD confirms
         `{ status: "registered" }` clears `checked_in_at` and that no separate
         undo command is coming. */
      updateGuest: (a) => api(`/v1/admin/guests/${enc(s(a, "guestId"))}`,
        { method: "PATCH", body: JSON.stringify(a.input ?? {}) }, "updateGuest"),
      addWalkupGuest: (a) => post(`/v1/admin/events/${enc(s(a, "eventId"))}/guests`, a.input, "addWalkupGuest"),
      checkInRegistrationGuest: (a) => post(
        `/v1/admin/events/${enc(s(a, "eventId"))}/registrations/${enc(s(a, "registrationId"))}/guests/${enc(s(a, "registrationGuestId"))}/check-in`,
        a.input, "checkInRegistrationGuest"),
    };
    const onMsg = (ev: MessageEvent) => {
      if (!tenantSessionCurrent()) return;
      if (ev.origin !== window.location.origin) return;
      const w = frameRef.current?.contentWindow;
      if (!w || ev.source !== w) return;
      const m = ev.data as { type?: string; requestId?: number; callId?: string; args?: Args } | null;
      if (!m || m.type !== "calendar-control-op") return;
      const reply = (body: Record<string, unknown>) => {
        try { w.postMessage({ type: "calendar-control-op-result", requestId: m.requestId, ...body }, window.location.origin); }
        catch { /* frame went away */ }
      };
      const run = m.callId && (!eventsOnly || EVENT_OPS.has(m.callId)) ? OPS[m.callId] : undefined;
      if (!run) { reply({ ok: false, error: `${m.callId ?? "(none)"} is not a bound operation` }); return; }
      void Promise.resolve()
        .then(() => tenantSessionCurrent() ? run(m.args ?? {}) : undefined)
        .then((data) => {
          /* A FULFILLED PROMISE IS NOT A SUCCESSFUL WRITE.
           *
           * The admin booking wrappers (`booking.ts` → `apiRaw` →
           * `mapMutationResponse`) RESOLVE an error envelope instead of throwing,
           * so a 501 `ROUTE_UNBOUND` on `/complete` arrived here as a fulfilled
           * call: the board closed the sheet and flashed "complete" for a visit
           * the server never accepted. That is the one failure the delta brief
           * forbids outright, and no amount of board-side honesty can reach it —
           * the seam has to refuse first.
           *
           * So an envelope carrying an `error` is reported as a failure, with the
           * server's own code and message. Anything else passes through
           * untouched: a shape check would have to know 20 response contracts. */
          const envelope = (data && typeof data === "object" ? data : null) as { error?: unknown } | null;
          const err = envelope?.error;
          if (err) {
            const e = (typeof err === "object" ? err : { message: String(err) }) as { code?: unknown; message?: unknown; status?: unknown };
            reply({
              ok: false,
              error: String(e.message ?? "") || String(e.code ?? "") || "the server refused the write",
              code: e.code ? String(e.code) : undefined,
              status: typeof e.status === "number" ? e.status : undefined,
            });
            return;
          }
          reply({ ok: true, data: data ?? null });
        })
        .catch((e: unknown) => {
          /* `ApiError.message` is DELIBERATELY ALLOWED TO BE EMPTY (client.ts:
             "message is honest and may be ''; fallbackLabel is the always-present
             one"), and `instanceof` lies across realms — which is why the client
             exports `isApiError`. Reading `e.message` through `instanceof Error`
             therefore sent the board an empty string for every refusal, so a 403
             denial, a validation refusal and an unbound route all rendered as the
             same generic sentence. The code and status ride along so the board can
             tell a denial from a missing route. */
          if (isApiError(e)) {
            reply({ ok: false, error: e.fallbackLabel || e.message || e.code, code: e.code, status: e.status });
            return;
          }
          reply({ ok: false, error: e instanceof Error ? (e.message || String(e)) : String(e) });
        });
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [tenantSession.slug, tenantSession.generation, tenantSession.signedIn, tenantSession.epoch]);

  /* Rail → board. The route changed under us (rail click, deep link, back
   * button), so tell the board which mode it is now showing. */
  useEffect(() => {
    if (!ready) return;
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ type: "calendar-control-set-mode", mode }, window.location.origin); } catch { /* noop */ }
  }, [mode, ready]);

  /* Students → My Day: the record's Appointment / Event / Queue buttons arrive
   * as route params; the board opens that form with the student prefilled.
   * `customerId` is the record's canonical id, forwarded when the route carries
   * one: a display name is not an identity, and a create that knows only the
   * name makes the server find-or-create a customer by whatever contact staff
   * retype. It reaches the booking only once the record's button sends it and
   * the board opens the form on it. */
  const createWhat = typeof nav.params.create === "string" ? nav.params.create : "";
  const createWho = typeof nav.params.who === "string" ? nav.params.who : "";
  const createCustomerId = typeof nav.params.customerId === "string" ? nav.params.customerId.trim() : "";
  useEffect(() => {
    if (!ready || !createWhat) return;
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    try {
      w.postMessage({ type: "calendar-control-create", what: createWhat, who: createWho,
        ...(createCustomerId ? { customerId: createCustomerId } : {}) }, window.location.origin);
    } catch { /* noop */ }
  }, [ready, createWhat, createWho, createCustomerId]);

  /* THE DEEPLINK (scan docket B): `?day=YYYY-MM-DD&entry=<entryId>` lands My Day
   * (or My Calendar) on that day with the element's sheet open — the route a scan
   * navigates through, so a shared link and a scan behave the same. */
  const dlDay = typeof nav.params.day === "string" ? nav.params.day : "";
  const dlEntry = typeof nav.params.entry === "string" ? nav.params.entry : "";
  useEffect(() => {
    if (!ready || (!dlDay && !dlEntry)) return;
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ type: "calendar-control-entry", day: dlDay, entry: dlEntry }, window.location.origin); } catch { /* noop */ }
  }, [ready, dlDay, dlEntry]);

  /* THE RAIL'S SCAN (KP, 2026-09-17): the phone bottom rail's QR button routes
   * here with `?scan=<nonce>` from any screen; the nonce makes a second tap on
   * the same screen re-fire. The board opens its scanner. */
  const scanNonce = typeof nav.params.scan === "string" ? nav.params.scan : "";
  useEffect(() => {
    if (!ready || !scanNonce) return;
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ type: "calendar-control-scan" }, window.location.origin); } catch { /* noop */ }
  }, [ready, scanNonce]);

  /* Board → rail. */
  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      if (!tenantSessionCurrent()) return;
      /* ORIGIN GATE — the board is served from our own origin, so this is an
       * exact match rather than an allowlist. Without it any window holding a
       * handle to Studio could drive the router. Same rule as FloorCalendar. */
      if (ev.origin !== window.location.origin) return;
      if (ev.source !== frameRef.current?.contentWindow) return;
      const m = ev.data as { type?: string; mode?: string; view?: string; date?: string; push?: boolean; focus?: string } | null;
      if (!m || typeof m !== "object") return;
      if (m.type === "calendar-control-ready") {
        readyRef.current = true;
        setBootFailure(null);
        setReady(true);
        try { frameRef.current?.contentWindow?.postMessage({ type: "calendar-control-permissions", canCreateAppointment, canEditOwnAppointment, canCancelOwnAppointment, ...(eventsOnly ? { scope: "events" } : {}), modules: { booking: bookingComposed, queue: queueComposed }, customersLabel }, window.location.origin); } catch { /* noop */ }
        return;
      }
      if (m.type === "calendar-control-mode") {
        const next = m.mode as CalendarMode;
        if (!next || next === mode) return;
        /* Events scope moves only between its own My Day and My Calendar: Floor / Schedule are
         * workforce surfaces these roles do not hold. Push the mode back so the board's tab follows. */
        if (eventsOnly && next !== "myday" && next !== "mine") {
          try { frameRef.current?.contentWindow?.postMessage({ type: "calendar-control-set-mode", mode }, window.location.origin); } catch { /* noop */ }
          return;
        }
        const route = routeOf(next);
        /* A clicked day rides along (wave 2a, #7): a Week day header opens THAT
         * day in My Day, not today. */
        const date = (next === "myday" || next === "mine") && typeof m.date === "string" && DATE_RE.test(m.date) ? m.date : "";
        /* A heading opened from the keyboard asks for My Day's heading (wave 4, JAI-2149): the new frame's src carries it. */
        pendingBoardFocus = next === "myday" && m.focus === "myday-heading" ? "myday-heading" : "";
        if (route) nav.go(route, date ? { date } : {});
        return;
      }
      /* The board's view/date changed on this route (wave 2a, #8): keep the URL
       * in step. A view change or a jump pushes (Back returns to it); Prev/Next
       * replaces. Same route, so the frame is not remounted. */
      if (m.type === "calendar-control-view") {
        if (m.mode !== mode || (mode !== "mine" && mode !== "myday")) return;
        const view = mode === "mine" && typeof m.view === "string" && VIEWS.has(m.view) ? m.view : "";
        const date = typeof m.date === "string" && DATE_RE.test(m.date) ? m.date : "";
        if (view === paramOf(nav.params, "view") && date === paramOf(nav.params, "date")) return;
        const params: Record<string, unknown> = {};
        if (view) params.view = view;
        if (date) params.date = date;
        boardRouteRef.current.push(view + "|" + date);
        if (m.push) nav.go(routeOf(mode), params); else nav.replace(routeOf(mode), params);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, nav, canCreateAppointment, canEditOwnAppointment, canCancelOwnAppointment, eventsOnly, tenantSession.slug, tenantSession.generation, tenantSession.signedIn, tenantSession.epoch]);

  /* Back / Forward on this route (wave 2a, #8): the query changed under the
   * board, so tell it what to show. A change the board itself just asked for
   * (boardRouteRef, in order) is not echoed back — that echo, arriving after a
   * second quick Next, would pull the board back a step. */
  const routeView = paramOf(nav.params, "view"), routeDate = paramOf(nav.params, "date");
  const loadingView: "day" | "week" | "month" = mode === "mine"
    ? (VIEWS.has(routeView) ? routeView as "day" | "week" | "month" : ((containerWidth || window.innerWidth) >= 760 ? "week" : "day"))
    : "day";
  useEffect(() => {
    if (!ready) return;
    const key = routeView + "|" + routeDate;
    const pending = boardRouteRef.current, at = pending.indexOf(key);
    if (at >= 0) { pending.splice(0, at + 1); return; }
    pending.length = 0;
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ type: "calendar-control-set-view", view: routeView, date: routeDate }, window.location.origin); } catch { /* noop */ }
  }, [ready, routeView, routeDate]);

  /* THE BOARD PRINTS IN THE WORKSPACE'S FORMATS (wave 4, JAI-2194 #45): the date pattern and clock the
   * rest of Studio uses, and the week start when it could be read, on ready and whenever one changes. */
  const dateTimeVersion = useDateTimeVersion();
  useEffect(() => {
    if (!ready) return;
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ type: "calendar-control-locale", ...boardLocale() }, window.location.origin); } catch { /* noop */ }
  }, [ready, dateTimeVersion, weekStartsOn]);

  /* Effective access can change while this iframe stays mounted (team-role edits,
   * grants/revokes, impersonation). The board treats a missing message as denied. */
  useEffect(() => {
    if (!ready) return;
    const w = frameRef.current?.contentWindow;
    if (!w) return;
    try { w.postMessage({ type: "calendar-control-permissions", canCreateAppointment, canEditOwnAppointment, canCancelOwnAppointment, ...(eventsOnly ? { scope: "events" } : {}), modules: { booking: bookingComposed, queue: queueComposed }, customersLabel }, window.location.origin); } catch { /* noop */ }
  }, [ready, canCreateAppointment, canEditOwnAppointment, canCancelOwnAppointment, user, bookingComposed, queueComposed, customersLabel]);

  /* A board document can open without ever reaching its ready handshake. Bound only this boot phase;
   * post-ready API errors remain owned by the board. Every callback is tied to the exact frame attempt. */
  useEffect(() => {
    if (!srcOpen || !boardSrc || ready || bootFailure || !tenantSessionCurrent()) return undefined;
    const frame = frameRef.current;
    if (!frame) return undefined;
    const attempt = bootAttempt;
    const sessionEpoch = tenantSession.epoch;
    const timer = window.setTimeout(() => {
      if (frameRef.current !== frame || bootAttemptRef.current !== attempt || readyRef.current || !tenantSessionCurrent()
        || tenantSession.epoch !== sessionEpoch || frame.getAttribute("src") !== boardSrc) return;
      setBootFailure("timeout");
    }, CALENDAR_BOOT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [srcOpen, boardSrc, ready, bootFailure, bootAttempt, tenantSession.slug, tenantSession.generation, tenantSession.signedIn, tenantSession.epoch]);

  const retryCalendarBoot = () => {
    if (!boardSrc || !tenantSessionCurrent()) return;
    liveSeq.current++;
    readyRef.current = false;
    setReady(false);
    setBootFailure(null);
    setBootAttempt((attempt) => attempt + 1);
  };

  return (
    <Screen rail={eventsOnly ? (mode === "mine" ? "ev-mycal" : "ev-myday") : RAIL_OF[mode]}>
      <div ref={containerRef} style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, position: "relative" }}>
        {!bootFailure && (!srcOpen || !ready || !tenantSessionCurrent()) && <CalendarControlLoading mode={mode} view={loadingView} width={containerWidth || window.innerWidth} />}
        {bootFailure && tenantSessionCurrent() && <div data-testid="my-calendar-boot-error" style={{ position: "absolute", inset: 8, zIndex: 3, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 16, background: "var(--jrni-color-surface-canvas)" }}>
          <Banner tone={bootFailure === "timeout" ? "warning" : "danger"} title={t("common.status.error", "Something went wrong")} dismissible={false} style={{ width: "min(100%, 560px)" }}>
            <div>{bootFailure === "timeout" ? t("workforce.calendar.openTimeout", "Calendar is taking longer to open. Try again.") : t("workforce.calendar.openFailed", "Calendar could not open. Try again.")}</div>
            <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}><Btn variant="secondary" size="lg" data-testid="my-calendar-boot-retry" onClick={retryCalendarBoot}>{t("common.actions.retry", "Retry")}</Btn></div>
          </Banner>
        </div>}
        <iframe
          key={`${tenantSession.epoch}:${tenantSession.generation}:${tenantSession.slug ?? "pending"}:${tenantSession.signedIn ? "in" : "out"}:${bootAttempt}`}
          ref={frameRef}
          title="Calendar"
          aria-hidden={!ready || !!bootFailure}
          tabIndex={!ready || !!bootFailure ? -1 : 0}
          onErrorCapture={(event) => {
            const frame = event.currentTarget;
            const failedAttempt = bootAttempt;
            if (frameRef.current === frame && bootAttemptRef.current === failedAttempt && !readyRef.current && tenantSessionCurrent()
              && frame.getAttribute("src") === boardSrc) setBootFailure("load-error");
          }}
          src={tenantSessionCurrent() ? boardSrc : undefined}
          /* THE CAMERA CROSSES THE FRAME ONLY IF DELEGATED (KP, 2026-09-17): without
           * `allow="camera"` every getUserMedia inside the board is refused and the
           * scanner falls back to its "camera was not allowed" note — in production
           * too, not only in the preview. */
          allow="camera"
          style={{ flex: 1, minHeight: 0, width: "100%", border: "none", background: "var(--jrni-color-surface-canvas)", position: "relative", zIndex: 1, display: srcOpen && tenantSessionCurrent() ? "block" : "none" }}
        />
      </div>
    </Screen>
  );
}
